import type { ChatResult, ChatUsage } from '../types';

/** Incremental SSE parser: handles split UTF-8, CRLF, multi-line data and
 *  comments. A truncated stream is an error, never a billable partial reply. */
export async function readChatStream(
  response: Response,
  format: 'openai' | 'anthropic' | 'gemini' | 'openline',
  onDelta: (text: string) => void,
): Promise<ChatResult> {
  if (!response.body) throw new Error('AI stream has no body');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let text = '';
  let complete = false;
  let input: number | undefined;
  let output: number | undefined;
  const append = (delta: unknown) => {
    if (typeof delta !== 'string' || !delta) return;
    if (text.length + delta.length > 200_000) throw new Error('AI response exceeds limit');
    text += delta;
    onDelta(delta);
  };
  const frame = (value: string) => {
    const data = value.split('\n').filter((line) => line.startsWith('data:')).map((line) => line.slice(5).trimStart()).join('\n');
    if (!data) return;
    if (data === '[DONE]') { complete = true; return; }
    const event = JSON.parse(data);
    if (event.error || event.type === 'error') throw new Error('AI stream reported an error');
    if (format === 'gemini') {
      const candidate = event.candidates?.[0];
      for (const part of candidate?.content?.parts ?? []) if (!part.thought) append(part.text);
      if (candidate?.finishReason) {
        if (!['STOP', 'MAX_TOKENS'].includes(candidate.finishReason)) throw new Error('AI stream refused');
        complete = true;
      }
      input = event.usageMetadata?.promptTokenCount ?? input;
      output = event.usageMetadata?.candidatesTokenCount ?? output;
    } else if (format === 'anthropic' || (format === 'openline' && event.type)) {
      if (event.type === 'content_block_start' && event.content_block?.type === 'text') append(event.content_block.text);
      if (event.type === 'content_block_delta' && event.delta?.type === 'text_delta') append(event.delta.text);
      input = event.message?.usage?.input_tokens ?? event.usage?.input_tokens ?? input;
      output = event.usage?.output_tokens ?? event.message?.usage?.output_tokens ?? output;
      if (event.type === 'message_stop') complete = true;
    } else {
      const choice = event.choices?.find((c: { index: number }) => c.index === 0);
      append(choice?.delta?.content);
      if (choice?.finish_reason && !['stop', 'length'].includes(choice.finish_reason)) throw new Error('AI stream refused');
      input = event.usage?.prompt_tokens ?? input;
      output = event.usage?.completion_tokens ?? output;
    }
  };
  try {
    while (true) {
      const chunk = await reader.read();
      buffer += decoder.decode(chunk.value, { stream: !chunk.done });
      // Normalize CRLF only after both characters arrived.
      buffer = buffer.replace(/\r\n/g, '\n');
      let end: number;
      while ((end = buffer.indexOf('\n\n')) >= 0) {
        frame(buffer.slice(0, end));
        buffer = buffer.slice(end + 2);
      }
      if (buffer.length > 1_000_000) throw new Error('AI stream frame exceeds limit');
      if (chunk.done) break;
    }
    if (buffer.trim()) frame(buffer);
    if (!complete) throw new Error('AI stream ended before completion');
    const usage: ChatUsage | undefined = Number.isSafeInteger(input) && Number.isSafeInteger(output) && input! >= 0 && output! >= 0
      ? { inputTokens: input!, outputTokens: output! } : undefined;
    return { text, ...(usage ? { usage } : {}) };
  } finally {
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}
